import { ROUTEMAP } from './../../components/fakeDoor/constants';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { API_ROUTES, NEXTJS_API_ROUTES } from '@/constants/apiPaths';
import Router from '@/components/common/router';
import { PageRoutes } from '@/constants/trackingStages';
import { IS_SLOT_BOOKING_ENABLED } from '@/components/fakeDoor/constants';
import { getUrlWithUtmParams } from '@/components/common/router';
import { consoleLog } from '@/utils/api';
import Cookies from 'js-cookie';
import { getAppointmentDetails } from '@/components/fakeDoor/api';
import { setAppointments } from './appointmentSlice';
import { getUserIdFromCookie } from '@/utils/common';
import {
    AUTH_ENTITY_TYPE,
    AUTH_KEYS,
    GARAGE_FRONTEND_SOURCE_ID,
} from '@/constants/common';

interface GarageState {
    timeline: any;
    loading: boolean;
    error: string | null;
    mockedStage: string | null;
    orderId: string | null;
}

const initialState: GarageState = {
    timeline: {},
    loading: false,
    error: null,
    orderId: null,
    mockedStage: null,
};

const garageRedirectPath = `${ROUTEMAP.GARAGE_REDIRECT}`;
const confirmationPagePath = ROUTEMAP.GARAGE_APPOINTMENT_CONFIRMATION;

const logWithUserInfo = (message: string) => {
    console.log(
        `timelineResponse platform: ${Cookies.get('webview')} user_id:${getUserIdFromCookie()} ${message}`
    );
};

export const fetchGarageTimeline = createAsyncThunk(
    'garage/fetchTimeline',
    async (orderId: string, thunkAPI) => {
        try {
            const options = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    [AUTH_KEYS.X_SOURCE_ID]: GARAGE_FRONTEND_SOURCE_ID,
                    [AUTH_KEYS.X_ENTITY]: [AUTH_ENTITY_TYPE.ORDER],
                    [AUTH_KEYS.X_ENTITY_ID]: orderId,
                },
                credentials: 'include',
            };
            const response = await fetch(
                `${NEXTJS_API_ROUTES.GARAGE_TIMELINE}/${orderId}`,
                options as any
            );
            if (!response.ok) {
                throw new Error('Failed to fetch garage timeline');
            }
            const parsedResponse = await response.json();
            // redirect to fake door in case there is no data available against the order id
            if (
                parsedResponse?.timeline === null &&
                parsedResponse?.latest_stage_details === null
            ) {
                window.location.href =
                    window.location.origin + IS_SLOT_BOOKING_ENABLED
                        ? `${process.env.NEXT_PUBLIC_ENV_PREFIX}${garageRedirectPath}`
                        : '/gi/fnol/ad-services';
                return null;
            }
            return parsedResponse;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

const redirectTo = (path: string, queryParams = {}) => {
    const fullUrl = getUrlWithUtmParams({
        url: `${process.env.NEXT_PUBLIC_ENV_PREFIX}${path}`,
        queryParams,
    });
    window.location.replace(fullUrl);
};

export const fetchOrderId = createAsyncThunk(
    'garage/fetchOrderId',
    async (isAckoGarage: boolean, { dispatch, getState }) => {
        logWithUserInfo('Fetching order ID');
        try {
            logWithUserInfo('inside try');
            const response = await fetch(API_ROUTES.FETCH_ORDERID, {
                method: 'GET',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                logWithUserInfo(`${JSON.stringify(response.status)} api !200`);
                throw new Error('Failed to fetch Order ID');
            }

            logWithUserInfo(`before parse ${JSON.stringify(response)}`);
            const res = await response.json();
            //await response.json();
            logWithUserInfo(`${JSON.stringify(res)} try success`);
            if (res?.booking_id && !res.order_id) {
                redirectTo(confirmationPagePath, {
                    booking_id: res.booking_id,
                });
                return;
            }

            if (res?.appointment_id && !res.order_id) {
                // const data = await getAppointmentDetails(
                //     res?.appointment_id as string
                // );
                // dispatch(setAppointments(data));
                // const appointmentDetails = getState()?.appointmentData;
                // console.log(` appointmentDetails`, appointmentDetails);

                //await Router.push(fullUrl);
                if (!isAckoGarage) {
                    redirectTo(garageRedirectPath, {
                        appointmentId: res.appointment_id,
                    });
                }

                //!isAckoGarage && (await Router.push(garageRedirectPath));
                return;
            }
            if (!res?.booking_id && !res?.order_id) {
                if (!isAckoGarage) {
                    redirectTo(garageRedirectPath);
                }
                return;
            }

            if (!res?.appointment_id && !res?.order_id) {
                if (!isAckoGarage) {
                    redirectTo(garageRedirectPath);
                }
                return;
            }
            return res.order_id;
        } catch (error: any) {
            logWithUserInfo(`${JSON.stringify(error)} catch`);
            // redirect to fake door in case there is no job id  available against the user
            if (!isAckoGarage) {
                redirectTo(garageRedirectPath);
            }

            return null;
        }
    }
);

const garageSlice = createSlice({
    name: 'garage',
    initialState,
    reducers: {
        setMockState(state, action) {
            state.mockedStage = action.payload;
        },
        setOrderId: (state, action) => {
            state.orderId = action.payload;
        },
        clearTimeline: (state, action) => {
            state.timeline = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchGarageTimeline.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchGarageTimeline.fulfilled, (state, action) => {
                state.loading = false;
                state.timeline = action.payload;
            })
            .addCase(fetchGarageTimeline.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                consoleLog(`Error fetching timeline details ${action.payload}`);
                Router.push(PageRoutes.ERROR);
            })
            .addCase(fetchOrderId.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchOrderId.fulfilled, (state, action) => {
                state.loading = false;
                state.orderId = action.payload;
            })
            .addCase(fetchOrderId.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
                Router.push(`${PageRoutes.ERROR}`);
            });
    },
});
export const { setOrderId } = garageSlice.actions;
export const { clearTimeline } = garageSlice.actions;

export const selectOrderId = (state: any) => state.garage.orderId;

export const selectTimeline = (state: any) => state.garage;
export const { setMockState } = garageSlice.actions;
export default garageSlice.reducer;
